import React from 'react';
import type { OrderWidgetData, OrderStatus } from '../../types/widgets';

// D-75: fulfillment ladder. `packed` removed (no producer ever set it).
// Statuses outside this set (processing / cancelled / returned / unknown)
// render as a pill in the header instead of as a progress strip.
const STEPS: Array<{ key: OrderStatus; label: string }> = [
  { key: 'placed', label: 'Order Placed' },
  { key: 'shipped', label: 'Shipped' },
  { key: 'out_for_delivery', label: 'Out for delivery' },
  { key: 'delivered', label: 'Delivered' },
];

// Off-ladder pill metadata (mirrors OrderListWidget.STATUS_PILL palette).
const OFF_LADDER_PILL: Partial<Record<OrderStatus, { label: string; color: string; bg: string }>> = {
  processing: { label: 'Processing Order', color: '#D97706', bg: '#FFFBEB' },
  cancelled: { label: 'Cancelled', color: '#DC2626', bg: '#FEF2F2' },
  returned: { label: 'Returned', color: '#7C3AED', bg: '#F5F3FF' },
  unknown: { label: 'Unknown', color: '#6B7280', bg: '#F9FAFB' },
};

function getStepIndex(status: OrderStatus): number {
  const idx = STEPS.findIndex((s) => s.key === status);
  return idx >= 0 ? idx : -1;
}

export interface OrderWidgetProps {
  data: OrderWidgetData;
}

export const OrderWidget: React.FC<OrderWidgetProps> = ({ data }) => {
  const { orderId, items, status, eta, trackingUrl, carrier } = data;
  const activeIdx = getStepIndex(status);
  const offLadderPill = OFF_LADDER_PILL[status];
  const isOffLadder = offLadderPill !== undefined;

  return (
    <div
      style={{
        width: '100%',
        maxWidth: 480,
        maxHeight: '100%',
        background: 'var(--voice-widget-card-bg, #FFFFFF)',
        border: '1px solid var(--voice-widget-border, #E5E7EB)',
        borderRadius: 16,
        padding: '18px 18px 14px',
        boxSizing: 'border-box',
        boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
        overflowY: 'auto',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 14,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.5,
              textTransform: 'uppercase',
              color: 'var(--voice-widget-muted, #6B7280)',
              marginBottom: 2,
            }}
          >
            Order
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--voice-widget-fg, #111827)' }}>
            #{orderId}
          </div>
        </div>
        {!isOffLadder && (
          <div style={{ textAlign: 'right' }}>
            <div
              style={{
                fontSize: 11,
                color: 'var(--voice-widget-muted, #6B7280)',
                marginBottom: 2,
              }}
            >
              Arriving
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--voice-widget-fg, #111827)' }}>
              {eta}
            </div>
          </div>
        )}
        {isOffLadder && offLadderPill && (
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: offLadderPill.color,
              background: offLadderPill.bg,
              padding: '3px 10px',
              borderRadius: 999,
            }}
          >
            {offLadderPill.label}
          </div>
        )}
      </div>

      {/* Progress strip */}
      {!isOffLadder && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
            {STEPS.map((step, i) => {
              const done = i <= activeIdx;
              const active = i === activeIdx;
              return (
                <React.Fragment key={step.key}>
                  {/* Node */}
                  <div
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      background: done
                        ? 'var(--voice-widget-accent, #7DD3C0)'
                        : 'var(--voice-widget-border, #E5E7EB)',
                      border: active ? '3px solid var(--voice-widget-accent, #7DD3C0)' : 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      zIndex: 1,
                      boxSizing: 'border-box',
                      transition: 'background 200ms ease-out',
                    }}
                  >
                    {done && (
                      <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
                        <path d="M1 4l3 3 6-6" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                  {/* Connector */}
                  {i < STEPS.length - 1 && (
                    <div
                      style={{
                        flex: 1,
                        height: 3,
                        background:
                          i < activeIdx
                            ? 'var(--voice-widget-accent, #7DD3C0)'
                            : 'var(--voice-widget-border, #E5E7EB)',
                        transition: 'background 200ms ease-out',
                      }}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Step labels */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: 6,
            }}
          >
            {STEPS.map((step, i) => (
              <div
                key={step.key}
                style={{
                  fontSize: 10,
                  fontWeight: i === activeIdx ? 700 : 400,
                  color:
                    i <= activeIdx
                      ? 'var(--voice-widget-accent-dark, #0D9488)'
                      : 'var(--voice-widget-muted, #6B7280)',
                  textAlign: 'center',
                  width: 48,
                  lineHeight: 1.2,
                }}
              >
                {step.label}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Items */}
      {items.slice(0, 2).map((item, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'center',
            marginBottom: 8,
          }}
        >
          {item.imageUrl && (
            <img
              src={item.imageUrl}
              alt={item.title}
              style={{
                width: 40,
                height: 40,
                objectFit: 'contain',
                borderRadius: 6,
                background: 'var(--voice-widget-img-bg, #F9FAFB)',
                flexShrink: 0,
              }}
            />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 500,
                color: 'var(--voice-widget-fg, #111827)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {item.title}
            </div>
            <div style={{ fontSize: 11, color: 'var(--voice-widget-muted, #6B7280)' }}>
              Qty {item.qty} · {item.price}
            </div>
          </div>
        </div>
      ))}

      {/* Carrier / tracking */}
      {carrier && (
        <div
          style={{
            borderTop: '1px solid var(--voice-widget-border, #E5E7EB)',
            marginTop: 8,
            paddingTop: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span style={{ fontSize: 12, color: 'var(--voice-widget-muted, #6B7280)' }}>
            Carrier: <strong style={{ color: 'var(--voice-widget-fg, #111827)' }}>{carrier}</strong>
          </span>
          {trackingUrl && (
            <a
              href={trackingUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                marginLeft: 'auto',
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--voice-widget-accent-dark, #0D9488)',
                textDecoration: 'none',
              }}
            >
              Track →
            </a>
          )}
        </div>
      )}
    </div>
  );
};

export default OrderWidget;
